<template>
    <div class="dd-rule-row-subjects">
        <span v-if="firstRule" class="rule-text-label">
            {{ $t('funnel.edit.decision.if.the.label') }}
        </span>
        <span v-else class="rule-text-label">
            {{ comparisonLabel }}
        </span>
        <DsMultiselect
            v-model="selectedSubject"
            data-qa="selected-subject-qa"
            :placeholder="$t('funnel.edit.decision.category.select.label')"
            :options="subjectOptions"
            class="ds-multiselect-wrapper selected-subject-class"
            @input="onSubjectChange"
        />

        <DsMultiselect
            v-if="isFieldCategoryVisible"
            v-model="selectedCategory"
            data-qa="selected-field-category-qa"
            :placeholder="$t('funnel.edit.decision.category.select.label')"
            :options="categoryOptions"
            class="ds-multiselect-wrapper field-category-class"
            @input="onCategoryChange"
        />

        <DsMultiselect
            v-if="isFieldVisible"
            v-model="selectedField"
            data-qa="selected-field-qa"
            :placeholder="$t('funnel.edit.decision.category.select.label')"
            :options="fieldOptions"
            class="ds-multiselect-wrapper field-class"
            @input="onFieldChange"
        />

        <DsMultiselect
            v-if="isConstraintVisible"
            v-model="selectedConstraint"
            data-qa="selected-constraint-qa"
            :placeholder="$t('funnel.edit.decision.category.select.label')"
            :options="constraintsOptions"
            class="ds-multiselect-wrapper selected-constraints-class"
            @input="onConstraintChange"
        />

        <div v-for="([valueMapKey, valueMapValue], index) in safeValueMapEntries" :key="valueMapValue.id">
            <DecisionDiamondRuleValue
                v-if="isAddingRuleValue"
                :index="index + 1"
                :is-first-element="index === 0"
                :value-map-key="parseInt(valueMapKey, 10)"
                :rule-value="valueMapValue"
                :rule-object="localRule"
                :dd-keys="ddKeys"
                :rule-map-key="ruleMapKey"
                :num-rule-values="ruleValuesCount"
                :input-type-value="inputTypeValue"
                :may-or-value="responseMayOrValue"
                :may-and-value="responseMayAndValue"
                @update-rule-value="updateRuleValue"
                @add-rule-value="addRuleValue"
                @delete-rule-value="deleteRuleValue"
            />
        </div>
    </div>
</template>

<script>
import DecisionDiamondRuleValue from './DecisionDiamondRuleValue.vue';
import {
    categoryAsField,
    categoryIsField,
    getChildren,
    getChildrenTagFirst,
    getInputType,
    getSubjects,
    mayAndValues,
    mayOrValues,
    saveRule,
    saveRuleValue,
} from './decision.api';

export default {
    components: {
        DecisionDiamondRuleValue,
    },
    props: {
        ruleMapValue: {
            type: Object,
            required: true,
        },
        ruleMapKey: {
            type: String,
            required: true,
        },
        firstRule: {
            type: Boolean,
            required: true,
        },
        comparison: {
            type: String,
            required: true,
        },
        ddKeys: {
            type: Object,
            required: true,
        },
    },
    emits: ['add-rule-value-global'],
    data() {
        return {
            localRule: null,
            selectedSubject: null,
            selectedCategory: null,
            selectedConstraint: null,
            selectedField: null,
            categoryOptions: [],
            subjectOptions: [],
            constraintsOptions: [],
            fieldOptions: [],
            isAddingRuleValue: false,
            inputTypeValue: '',
            responseMayOrValue: false,
            responseMayAndValue: false,
        };
    },
    computed: {
        safeValueMapEntries() {
            return this.ruleMapValue && this.ruleMapValue.valueMap ? Object.entries(this.ruleMapValue.valueMap) : [];
        },
        isFieldCategoryVisible() {
            return this.selectedSubject !== null;
        },
        isConstraintVisible() {
            return this.selectedConstraint !== null;
        },
        isFieldVisible() {
            return this.selectedField !== null;
        },
        ruleValuesCount() {
            return Object.keys(this.ruleMapValue?.valueMap).length;
        },
        comparisonLabel() {
            return `${this.comparison.toUpperCase()} ${this.$t('funnel.edit.decision.if.the.label')}`;
        },
    },
    async mounted() {
        this.localRule = { id: this.ruleMapKey, ...this.ruleMapValue };
        await this.loadMayOrAndValues();
        await this.setInputTypeValue();
        await this.loadRule();
    },
    methods: {
        async loadRule() {
            const {
                category,
                constraint,
                subject,
                field,
            } = this.localRule;

            const categoryIsFieldValue = await this.verifyCategoryIsField(category);
            let fieldFilled = field;

            if (categoryIsFieldValue) {
                fieldFilled = await categoryAsField({ category });
            }

            const { map: subjects } = await getSubjects(this.ddKeys);

            this.subjectOptions = Object.entries(subjects).map(([value, label]) => ({
                value,
                label,
            })).filter(({ label }) => label !== '');

            if (subject) {
                this.selectedSubject = this.subjectOptions.find(({ value }) => value === subject);

                if (subject === 'contact_Subject') {
                    const { map: categories } = await getChildrenTagFirst({ fieldKey: subject, ...this.ddKeys });

                    this.categoryOptions = Object.entries(categories).map(([value, label]) => ({
                        value,
                        label,
                    })).filter(({ label }) => label !== '');
                } else {
                    const { map: categories } = await getChildren({ fieldKey: subject, ...this.ddKeys });

                    this.categoryOptions = Object.entries(categories).map(([value, label]) => ({
                        value,
                        label,
                    })).filter(({ label }) => label !== '');
                }
                this.selectedCategory = this.categoryOptions.find(({ value }) => value === category);

                if (category) {
                    if (!categoryIsFieldValue) {
                        const { map: fieldsMap } = await getChildren({ fieldKey: category, ...this.ddKeys });

                        this.fieldOptions = Object.entries(fieldsMap).map(([value, label]) => ({
                            value,
                            label,
                        })).filter(({ label }) => label !== '');
                        this.selectedField = this.fieldOptions.find(({ value }) => value === fieldFilled);
                    }

                    if (fieldFilled) {
                        const { map: contraintsMap } = await getChildren({ fieldKey: fieldFilled, ...this.ddKeys });

                        this.constraintsOptions = Object.entries(contraintsMap).map(([value, label]) => ({
                            value,
                            label,
                        })).filter(({ label }) => label !== '');
                        this.selectedConstraint = this.constraintsOptions.find(({ value }) => value === constraint);

                        if (constraint) {
                            this.isAddingRuleValue = true;
                        }
                    }
                }
            }
        },

        async saveRuleWhenChanging() {
            const payload = {
                rule: { ...this.localRule },
                ...this.ddKeys,
            };

            const ruleSaved = await saveRule(payload);

            this.localRule = { ...ruleSaved.rule };

            this.$emit('add-rule-value-global', { ruleMapKey: this.ruleMapKey, rule: this.localRule });
        },

        async saveRuleValueChanging(valueMap) {
            const payload = {
                ruleValue: valueMap,
                allowDelete: true,
                ...this.ddKeys,
            };

            const { rule, ruleValue: ruleValueSaved } = await saveRuleValue(payload);

            this.localRule = {
                ...rule,
                valueMap: {
                    ...this.localRule.valueMap,
                    [ruleValueSaved.id]: ruleValueSaved,
                },
            };
        },

        updateRuleValue({ ruleValue: valueMap }) {
            this.saveRuleValueChanging(valueMap);
        },

        addRuleValue(rule) {
            this.localRule = { ...rule };
            this.$emit('add-rule-value-global', { ruleMapKey: this.ruleMapKey, rule: this.localRule });
        },

        deleteRuleValue(rule) {
            this.localRule = { ...rule };
            this.$emit('add-rule-value-global', { ruleMapKey: this.ruleMapKey, rule: this.localRule });
        },

        updateRuleLocal(payload, removeRuleValues = false) {
            const ruleTemp = { ...this.localRule, ...payload };

            this.localRule = ruleTemp;

            if (removeRuleValues) {
                for (const key of Object.keys(this.localRule.valueMap)) {
                    delete this.localRule.valueMap[key];
                }
                delete this.localRule.valueMap;
                this.localRule.valueMap = {};
            }
        },

        async onSubjectChange() {
            this.updateRuleLocal({
                subject: null, category: null, field: null, constraint: null,
            }, true);
            this.selectedCategory = null;
            this.selectedField = null;
            this.selectedConstraint = null;
            this.categoryOptions = [];
            this.fieldOptions = [];
            this.constraintsOptions = [];
            this.isAddingRuleValue = false;

            const { value: subject } = this.selectedSubject;

            const { map: categories } = await getChildrenTagFirst({ fieldKey: subject, ...this.ddKeys });

            this.categoryOptions = Object.entries(categories).map(([value, label]) => ({
                value,
                label,
            })).filter(({ label }) => label !== '');

            this.selectedCategory = null;
            this.updateRuleLocal({
                subject, category: null, field: null, constraint: null,
            });
            await this.saveRuleWhenChanging();
        },

        async onCategoryChange() {
            this.updateRuleLocal({
                category: null, field: null, constraint: null,
            }, true);
            this.selectedField = null;
            this.selectedConstraint = null;
            this.fieldOptions = [];
            this.constraintsOptions = [];
            this.isAddingRuleValue = false;

            const {
                constraint,
                field,
            } = this.localRule;

            const { value: category } = this.selectedCategory;

            const categoryIsFieldValue = await this.verifyCategoryIsField(category);
            let fieldFilled = field;

            if (categoryIsFieldValue) {
                fieldFilled = await categoryAsField({ category });
            }

            if (category) {
                if (!categoryIsFieldValue) {
                    const { map: fieldsMap } = await getChildren({ fieldKey: category, ...this.ddKeys });

                    this.fieldOptions = Object.entries(fieldsMap).map(([value, label]) => ({
                        value,
                        label,
                    })).filter(({ label }) => label !== '');
                    this.selectedField = this.fieldOptions.find(({ value }) => value === fieldFilled);

                    this.selectedConstraint = null;
                    this.constraintsOptions = [];
                } else if (fieldFilled) {
                    const { map: contraintsMap } = await getChildren({ fieldKey: fieldFilled, ...this.ddKeys });

                    this.constraintsOptions = Object.entries(contraintsMap).map(([value, label]) => ({
                        value,
                        label,
                    })).filter(({ label }) => label !== '');
                    this.selectedConstraint = this.constraintsOptions.find(({ value }) => value === constraint);

                    if (constraint) {
                        this.isAddingRuleValue = true;
                    }

                    this.selectedField = null;
                    this.fieldOptions = [];
                }
            }
            this.updateRuleLocal({
                category, field: fieldFilled, constraint,
            }, true);
            await this.saveRuleWhenChanging();
        },
        async onFieldChange() {
            this.selectedConstraint = null;
            this.constraintsOptions = [];
            this.isAddingRuleValue = false;

            const { value: field, constraint } = this.selectedField;

            if (field) {
                const { map: contraintsMap } = await getChildren({ fieldKey: field, ...this.ddKeys });

                this.constraintsOptions = Object.entries(contraintsMap).map(([value, label]) => ({
                    value,
                    label,
                })).filter(({ label }) => label !== '');
                this.selectedConstraint = this.constraintsOptions.find(({ value }) => value === constraint);

                if (constraint) {
                    this.isAddingRuleValue = true;
                }
            }

            this.updateRuleLocal({ field, constraint: null });
            await this.saveRuleWhenChanging();
        },
        async onConstraintChange() {
            this.isAddingRuleValue = false;

            const { value: constraint } = this.selectedConstraint;

            this.updateRuleLocal({ constraint }, true);
            await this.saveRuleWhenChanging();

            await this.loadMayOrAndValues();
            await this.setInputTypeValue();

            if (this.inputTypeValue === 'hidden') {
                this.isAddingRuleValue = false;
            } else {
                this.isAddingRuleValue = true;
            }
        },
        async verifyCategoryIsField(category) {
            const payload = {
                category,
                fieldKey: category,
                ...this.ddKeys,
            };
            const categoryIsFieldValue = await categoryIsField(payload);

            return categoryIsFieldValue;
        },
        async setInputTypeValue() {
            const {
                field: fieldKey,
                constraint,
            } = this.localRule;

            this.inputTypeValue = await getInputType({ fieldKey, ...this.ddKeys, constraint });
        },
        async loadMayOrAndValues() {
            const { constraint } = this.localRule;

            this.responseMayOrValue = await mayOrValues(constraint);
            this.responseMayAndValue = await mayAndValues(constraint);
        },
    },
};

</script>

<style lang="scss" scoped>
.rule-text-label {
    font-weight: bold;
    padding-top: $spacing-125;
}

.dd-rule-row-subjects {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-075;
    margin-right: auto;

    div.modal-body,
    div.multiselect-tags > span > label,
    div.ds-multiselect-wrapper > div > div > label  {
        padding-top: 0;
    }
}

.ds-multiselect-wrapper {
    margin-right: $spacing-125;
    width: auto;
    min-width: px-to-rem(100);
    max-width: 100%;
}

.add-rule-value {
    color: $color-blue;
    margin-right: px-to-rem(7);
    font-weight: bold;
}

.dd-rule-row-subjects .multiselect .multiselect-single {
    overflow: hidden;
}
</style>
